import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from './useWorkspace';
import { useToast } from '@/hooks/use-toast';

export interface WebhookSubscription {
  id: string;
  workspace_id: string;
  webhook_name: string;
  endpoint_url: string;
  events: string[];
  is_active: boolean;
  secret_key?: string;
  headers: Record<string, string>;
  retry_config: {
    max_retries: number;
    backoff_multiplier: number;
  };
  last_triggered_at?: string;
  success_count: number;
  failure_count: number;
  created_at: string;
  updated_at: string;
}

export interface WebhookDelivery {
  id: string;
  subscription_id: string;
  event_type: string;
  payload: Record<string, any>;
  status_code?: number;
  response_body?: string;
  delivered_at?: string;
  retry_count: number;
  next_retry_at?: string;
  created_at: string;
}

export const useTumWebhooks = () => {
  const workspace = useWorkspace();
  const currentWorkspace = workspace?.currentWorkspace;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch subscriptions
  const subscriptionsQuery = useQuery({
    queryKey: ['tum-webhook-subscriptions', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      
      const { data, error } = await supabase
        .from('tum_webhook_subscriptions')
        .select('*')
        .eq('workspace_id', currentWorkspace.id)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return data as WebhookSubscription[];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch deliveries
  const deliveriesQuery = useQuery({
    queryKey: ['tum-webhook-deliveries', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      
      // Get subscription IDs first
      const { data: subs } = await supabase
        .from('tum_webhook_subscriptions')
        .select('id')
        .eq('workspace_id', currentWorkspace.id);
      
      if (!subs || subs.length === 0) return [];
      
      const subscriptionIds = subs.map(s => s.id);
      
      const { data, error } = await supabase
        .from('tum_webhook_deliveries')
        .select('*')
        .in('subscription_id', subscriptionIds)
        .order('created_at', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      return data as WebhookDelivery[];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Create subscription
  const createSubscription = useMutation({
    mutationFn: async (subscription: Omit<WebhookSubscription, 'id' | 'workspace_id' | 'created_at' | 'updated_at' | 'success_count' | 'failure_count' | 'last_triggered_at'>) => {
      if (!currentWorkspace?.id) throw new Error('No workspace selected');
      
      const { data, error } = await supabase
        .from('tum_webhook_subscriptions')
        .insert([{
          ...subscription,
          workspace_id: currentWorkspace.id,
        }])
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-webhook-subscriptions'] });
      toast({
        title: "Webhook Created",
        description: "Webhook subscription has been created successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Create Webhook",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Update subscription
  const updateSubscription = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<WebhookSubscription> & { id: string }) => {
      const { data, error } = await supabase
        .from('tum_webhook_subscriptions')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-webhook-subscriptions'] });
      toast({
        title: "Webhook Updated",
        description: "Webhook settings have been updated.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Update Webhook",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Delete subscription
  const deleteSubscription = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tum_webhook_subscriptions')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-webhook-subscriptions'] });
      toast({
        title: "Webhook Deleted",
        description: "Webhook subscription has been removed.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Delete Webhook",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  // Test webhook
  const testWebhook = useMutation({
    mutationFn: async (subscriptionId: string) => {
      const { data, error } = await supabase.functions.invoke('tum-webhook-dispatcher', {
        body: {
          subscriptionId,
          testMode: true,
          testPayload: {
            event: 'test',
            timestamp: new Date().toISOString(),
            data: { message: 'This is a test webhook delivery' },
          },
        },
      });
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-webhook-deliveries'] });
      toast({
        title: "Test Webhook Sent",
        description: "Check the delivery log for results.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Test Failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    subscriptions: subscriptionsQuery.data || [],
    deliveries: deliveriesQuery.data || [],
    isLoading: subscriptionsQuery.isLoading || deliveriesQuery.isLoading,
    error: subscriptionsQuery.error || deliveriesQuery.error,
    createSubscription: createSubscription.mutate,
    updateSubscription: updateSubscription.mutate,
    deleteSubscription: deleteSubscription.mutate,
    testWebhook: testWebhook.mutate,
    isCreating: createSubscription.isPending,
    isUpdating: updateSubscription.isPending,
    isDeleting: deleteSubscription.isPending,
    isTesting: testWebhook.isPending,
  };
};
